import type { LayoutHotspotListItem } from '../types/types';

export interface HotspotTableRow {
  id: string;
  layoutPhotoLabel: string;
  title: string;
  createdBy: string;
  associatedObjectsCount: number;
  rawData: LayoutHotspotListItem;
}

/**
 * Transform layout hotspot list item into table row (Layout name + photo index, Title, Created By, Associated objects count).
 */
export const transformHotspotForTable = (hotspot: LayoutHotspotListItem): HotspotTableRow => {
  const layoutName = hotspot.layout_name ?? '—';
  const photoIndex = hotspot.photo_index != null ? String(hotspot.photo_index) : '—';
  const layoutPhotoLabel = `${layoutName} – ${photoIndex}`;

  return {
    id: String(hotspot.id),
    layoutPhotoLabel,
    title: hotspot.title ?? `Hotspot ${hotspot.id}`,
    createdBy: hotspot.created_by_display ?? '—',
    associatedObjectsCount: hotspot.overlay_count ?? 0,
    rawData: hotspot,
  };
};

/**
 * Build a single searchable string from a hotspot for filtering.
 */
function toSearchableString(hotspot: LayoutHotspotListItem): string {
  return [
    hotspot.title ?? '',
    hotspot.created_by_display ?? '',
    hotspot.layout_name ?? '',
    hotspot.photo_index != null ? String(hotspot.photo_index) : '',
  ]
    .join(' ')
    .toLowerCase();
}

/**
 * Filter hotspots by search term (title, created by, layout name, photo index).
 */
export const filterHotspots = (
  hotspots: LayoutHotspotListItem[],
  searchTerm: string
): LayoutHotspotListItem[] => {
  const trimmed = searchTerm.trim();
  if (!trimmed) return hotspots;
  const terms = trimmed.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return hotspots;
  return hotspots.filter((hotspot) => {
    const searchable = toSearchableString(hotspot);
    return terms.every((term) => searchable.includes(term));
  });
};

/**
 * No-op for compatibility: status filter not used in ironview360.
 */
export const filterHotspotsByStatus = (
  hotspots: LayoutHotspotListItem[],
  _statusFilter: string
): LayoutHotspotListItem[] => hotspots;

/**
 * No-op for compatibility: category filter not used in ironview360.
 */
export const filterHotspotsByCategory = (
  hotspots: LayoutHotspotListItem[],
  _categoryId: number | null
): LayoutHotspotListItem[] => hotspots;

/**
 * No-op for compatibility: subcategory filter not used in ironview360.
 */
export const filterHotspotsBySubcategory = (
  hotspots: LayoutHotspotListItem[],
  _subcategoryId: number | null
): LayoutHotspotListItem[] => hotspots;
